import React, { ChangeEvent, ReactElement, useEffect, useState } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';

import { ARCHIVED, UPCOMING } from '../../../../utils/constants';
import { formatUsername, getClassNames } from '../../../../utils/util';
import { draftContentToHtml, htmlContentToDraft } from '../../../../utils/text-editor-util';
import { checkForSupportChanges } from '../../../../utils/diff-util';

import { MessageOverview, SupportTemplate } from '../../../../types/entity.types';

import ProgrammeLayout from '../../../../layouts/programme/ProgrammeLayout';
import TextInput from '../../../common/input/text-input/TextInput';
import RichTextEditor from '../../../common/rich-text-editor/RichTextEditor';
import SaveChangesModal from '../../../common/modal/save-changes-modal/SaveChangesModal';

import { ReactComponent as SaveIcon } from '../../../../resources/icons/save.svg';
import { ReactComponent as GearIcon } from '../../../../resources/icons/gear.svg';
import { ReactComponent as CloseIcon } from '../../../../resources/icons/close.svg';
import { ReactComponent as SearchIcon } from '../../../../resources/icons/search.svg';
import { ReactComponent as TrashIcon } from '../../../../resources/icons/trash.svg';

import './support.scss';

type Props = {
  status: string;
  messageOverviews: MessageOverview[];
  supportTemplate: SupportTemplate;
  openMessages: (value: string) => void;
  createSupportTemplate: (template: SupportTemplate, url: string | undefined) => void;
  updateSupportTemplate: (template: SupportTemplate, url: string | undefined) => void;
  deleteMessage: (value: string) => void;
};

const Support = ({
  status,
  messageOverviews = [],
  supportTemplate,
  openMessages,
  createSupportTemplate,
  updateSupportTemplate,
  deleteMessage
}: Props): ReactElement => {
  const [settingsOpen, setSettingsOpen] = useState<boolean>(false);
  const [redirectUrl, setRedirectUrl] = useState<string | undefined>(undefined);
  const [filterValue, setFilterValue] = useState<string>('');
  const [filteredMessageOverviews, setFilteredMessageOverviews] = useState<MessageOverview[]>([]);
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [editorState, setEditorState] = useState<object | undefined>(undefined);

  const navigate: NavigateFunction = useNavigate();

  useEffect((): void => {
    if (supportTemplate) {
      setFirstName(supportTemplate.firstName);
      setLastName(supportTemplate.lastName);
      setEmail(supportTemplate.email);
      setPhone(supportTemplate.phone);
      setEditorState(htmlContentToDraft(supportTemplate.frequentlyAskedQuestionsString));
    }
  }, [supportTemplate]);

  useEffect((): void => {
    setFilteredMessageOverviews(
      messageOverviews.filter((messageOverview: MessageOverview): boolean => {
        const value: string = filterValue.toLowerCase();
        const { userName, message }: Partial<MessageOverview> = messageOverview;

        if (userName.toLowerCase().includes(value)) {
          return true;
        }

        return message.toLowerCase().includes(value);
      })
    );
  }, [filterValue, messageOverviews]);

  const checkForUnsavedChanges = (url: string): void => {
    let frequentlyAskedQuestionsString: string | undefined | null = draftContentToHtml(editorState);

    if (frequentlyAskedQuestionsString === undefined) frequentlyAskedQuestionsString = null;

    const supportTemplateToSave: SupportTemplate = {
      ...supportTemplate,
      firstName,
      lastName,
      email,
      phone,
      frequentlyAskedQuestionsString
    };

    if (checkForSupportChanges(supportTemplate, supportTemplateToSave)) {
      setRedirectUrl(url);
    } else {
      navigate(url);
    }
  };

  const handleSaveClick = (): void => {
    const supportTemplateToSave: SupportTemplate = {
      ...supportTemplate,
      firstName,
      lastName,
      email,
      phone,
      frequentlyAskedQuestionsString: draftContentToHtml(editorState)
    };

    if (supportTemplate?.externalReference) {
      const supportTemplateToUpdate: SupportTemplate = { ...supportTemplateToSave };
      updateSupportTemplate(supportTemplateToUpdate, redirectUrl);
    } else {
      createSupportTemplate(supportTemplateToSave, redirectUrl);
    }

    setRedirectUrl(undefined);
  };

  const handleDeleteClick = (e: Event, userExternalReference: string): void => {
    e.stopPropagation();
    deleteMessage(userExternalReference);
  };

  return (
    <ProgrammeLayout onNavigate={checkForUnsavedChanges}>
      <div className="support" data-testid={`support-${status}`}>
        <div className="support__icons">
          {((settingsOpen && status !== ARCHIVED) || status === UPCOMING) && (
            <div className="support__icon-container" onClick={handleSaveClick}>
              <SaveIcon className="support__save-icon" data-testid="save-icon" />
            </div>
          )}
          {!settingsOpen && status !== UPCOMING && (
            <div className="support__icon-container" onClick={(): void => setSettingsOpen(true)}>
              <GearIcon className="support__gear-icon" data-testid="cog-icon" />
            </div>
          )}
          {settingsOpen && status !== UPCOMING && (
            <div className="support__icon-container" onClick={(): void => setSettingsOpen(false)}>
              <CloseIcon className="support__close-icon" data-testid="close-icon" />
            </div>
          )}
        </div>

        {!settingsOpen && status !== UPCOMING && (
          <div>
            <div className="support__header">
              <h1>Inbox</h1>
            </div>
            <div className="support__chat-filter">
              <SearchIcon />
              <input
                value={filterValue}
                onChange={({ target }: ChangeEvent<HTMLInputElement>): void => setFilterValue(target.value)}
                data-testid="chat-filter"
              />
            </div>
            {filteredMessageOverviews.length > 0 && (
              <MessageOverviews
                status={status}
                messageOverviews={filteredMessageOverviews}
                openMessages={openMessages}
                handleDeleteClick={handleDeleteClick}
              />
            )}
            {filteredMessageOverviews.length === 0 && <span className="support__no-messages">No Messages</span>}
          </div>
        )}
        {(settingsOpen || status === UPCOMING) && (
          <div className="support__settings">
            <div className="support__header">
              <h1>Contact Details</h1>
            </div>
            <div className="support__contact-details">
              <TextInput
                label="First Name:"
                name="first-name"
                value={firstName}
                onChange={({ target }: ChangeEvent<HTMLInputElement>): void => setFirstName(target.value)}
                disabled={status === ARCHIVED}
              />
              <TextInput
                label="Last Name:"
                name="last-name"
                value={lastName}
                onChange={({ target }: ChangeEvent<HTMLInputElement>): void => setLastName(target.value)}
                disabled={status === ARCHIVED}
              />
              <TextInput
                label="Email:"
                name="email"
                value={email}
                onChange={({ target }: ChangeEvent<HTMLInputElement>): void => setEmail(target.value)}
                disabled={status === ARCHIVED}
              />
              <TextInput
                label="Phone:"
                name="phone"
                value={phone}
                onChange={({ target }: ChangeEvent<HTMLInputElement>): void => setPhone(target.value)}
                disabled={status === ARCHIVED}
              />
            </div>
            <hr className="support__divider" />
            <div className="support__faq">
              <h1 className="support__faq__header">FREQUENTLY ASKED QUESTIONS</h1>
              <div className="faq__text-editor" data-testid="faq-text-editor">
                <RichTextEditor
                  editorState={editorState}
                  setEditorState={setEditorState}
                  disabled={status === ARCHIVED}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <SaveChangesModal redirectUrl={redirectUrl} handleSaveClick={handleSaveClick} />
    </ProgrammeLayout>
  );
};

type MessageOverviewProps = {
  status: string;
  messageOverviews: MessageOverview[];
  openMessages: (value: string) => void;
  handleDeleteClick: (e: Event, userExternalReference: string) => void;
};

const MessageOverviews = ({
  status,
  messageOverviews,
  openMessages,
  handleDeleteClick
}: MessageOverviewProps): ReactElement => (
  <div className="support__chat-list">
    {messageOverviews.map((messageOverview: MessageOverview, index: number): ReactElement => {
      const { userExternalReference, date, message, userName, read }: MessageOverview = messageOverview;

      return (
        <div className="support__chat-list__item" key={index} onClick={(): void => openMessages(userExternalReference)}>
          <p className={getClassNames('support__chat-list__item__from', { read })}>{formatUsername(userName)}</p>
          <p className={getClassNames('support__chat-list__item__message', { read })}>{message}</p>
          <div
            className={getClassNames('support__chat-list__item__action', {
              archived: status === ARCHIVED
            })}
          >
            <p>{date}</p>
            <TrashIcon
              onClick={(e: Event): void => handleDeleteClick(e, userExternalReference)}
              data-testid="trash-icon"
            />
          </div>
        </div>
      );
    })}
  </div>
);

export default Support;
